/** Storage sidebar — folder tree with create folder button */

import { useState } from 'react'
import { FolderPlus, HardDrive, Loader2 } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useStorageFolderTree, useCreateStorageFolder } from '../../hooks/use-storage'
import { StorageFolderNode } from './storage-folder-node'

interface StorageSidebarProps {
  activeFolderId: string | null
  onSelect: (id: string | null) => void
  isDark: boolean
  canManage: boolean
}

export function StorageSidebar({
  activeFolderId,
  onSelect,
  isDark,
  canManage,
}: StorageSidebarProps) {
  const { data, isLoading } = useStorageFolderTree()
  const createFolder = useCreateStorageFolder()
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')

  const handleCreateSubmit = async () => {
    if (!canManage) return
    const name = newName.trim()
    if (!name) {
      setCreating(false)
      return
    }
    await createFolder.mutateAsync({ name }).catch(console.error)
    setNewName('')
    setCreating(false)
  }

  const folders = data?.folders ?? []

  return (
    <div
      className={cn(
        'flex h-full w-[220px] shrink-0 flex-col border-r',
        isDark ? 'border-white/[0.06] bg-surface-1' : 'border-neutral-200 bg-neutral-50',
      )}
    >
      {/* Header */}
      <div
        className={cn(
          'flex items-center justify-between px-3 py-2 border-b',
          isDark ? 'border-white/[0.06]' : 'border-neutral-200',
        )}
      >
        <span
          className={cn('text-xs font-semibold', isDark ? 'text-neutral-300' : 'text-neutral-600')}
        >
          Folders
        </span>
        {canManage && (
          <button
            onClick={() => setCreating(true)}
            className={cn(
              'rounded p-0.5',
              isDark
                ? 'text-neutral-500 hover:text-neutral-300'
                : 'text-neutral-400 hover:text-neutral-700',
            )}
            title="New folder"
          >
            <FolderPlus className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Folder tree */}
      <div className="flex-1 overflow-y-auto px-1 py-1">
        {/* Root item */}
        <button
          onClick={() => onSelect(null)}
          className={cn(
            'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs',
            activeFolderId === null
              ? 'bg-brand-600 text-white'
              : isDark
                ? 'text-neutral-300 hover:bg-surface-3'
                : 'text-neutral-700 hover:bg-neutral-100',
          )}
        >
          <HardDrive className="h-3.5 w-3.5 shrink-0" />
          <span>All files</span>
        </button>

        {isLoading && (
          <div className="flex items-center justify-center py-4">
            <Loader2
              className={cn(
                'h-4 w-4 animate-spin',
                isDark ? 'text-neutral-500' : 'text-neutral-400',
              )}
            />
          </div>
        )}

        {folders.map((folder) => (
          <StorageFolderNode
            key={folder.id}
            folder={folder}
            depth={0}
            activeFolderId={activeFolderId}
            onSelect={onSelect}
            isDark={isDark}
            canManage={canManage}
          />
        ))}

        {/* New folder inline input */}
        {canManage && creating && (
          <div
            className={cn(
              'flex items-center gap-1 rounded-lg px-2 py-1',
              isDark ? 'bg-surface-3' : 'bg-neutral-100',
            )}
          >
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onBlur={handleCreateSubmit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateSubmit()
                if (e.key === 'Escape') {
                  setCreating(false)
                  setNewName('')
                }
              }}
              placeholder="Folder name"
              className={cn(
                'flex-1 bg-transparent text-xs outline-none',
                isDark
                  ? 'text-neutral-100 placeholder-neutral-500'
                  : 'text-neutral-900 placeholder-neutral-400',
              )}
            />
          </div>
        )}
      </div>
    </div>
  )
}
